import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Crown, DollarSign, Package, CheckCircle2, AlertCircle, Trash2, Star, Truck } from "lucide-react";
import { toast } from "sonner";

function img(p) {
  if (!p) return "https://static.prod-images.emergentagent.com/jobs/d3a3c830-7d3a-49ef-ad18-3f342dc4beda/images/61fe03154047828097ebbc7961c5f590c8897894b9ea6fa9e207bb0d1fee5801.png";
  if (typeof p === "string" && p.startsWith("http")) return p;
  return fileUrl(p);
}

function isFeatured(p) {
  return p.featured_until && new Date(p.featured_until).getTime() > Date.now();
}

export default function SellerDashboard() {
  const { user, refresh: refreshAuth } = useAuth();
  const [products, setProducts] = useState([]);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get("/seller/products");
    setProducts(data || []);
  };
  useEffect(() => { load(); }, []);

  const payListing = async (productId) => {
    try {
      const { data } = await api.post("/checkout/listing-fee", { product_id: productId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error("Failed to start payment"); }
  };

  const boostListing = async (productId) => {
    try {
      const { data } = await api.post("/checkout/feature-listing", { product_id: productId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error("Could not start boost"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  const subActive = user?.seller_subscription_active;
  const subUntil = user?.seller_subscription_until ? new Date(user.seller_subscription_until).toLocaleDateString() : null;

  return (
    <div data-testid="seller-dashboard-page" className="pt-28 pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Seller dashboard</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">Welcome, {user?.name}.</h1>
        </div>
        <div className="flex gap-3">
          <Link to="/seller/orders" data-testid="goto-fulfillment" className="btn-secondary inline-flex items-center gap-2"><Truck className="w-4 h-4" /> Orders</Link>
          <Link to="/seller/sales" data-testid="goto-sales" className="btn-secondary inline-flex items-center gap-2"><DollarSign className="w-4 h-4" /> Sales</Link>
          <Link to="/seller/upgrade" data-testid="goto-upgrade" className={`btn-secondary inline-flex items-center gap-2 ${subActive ? "border-[#CCFF00] text-[#CCFF00]" : ""}`}>
            <Crown className="w-4 h-4" /> {subActive ? "Pro Active" : "Go Pro"}
          </Link>
          <Link to="/seller/new" data-testid="add-product-btn" className="btn-primary inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New Product</Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <div className="card-surface p-6">
          <Package className="w-4 h-4 text-[#FF007F] mb-3" />
          <p className="text-xs uppercase tracking-widest text-zinc-500">Listings</p>
          <p className="font-display font-black text-3xl mt-1">{products.length}</p>
        </div>
        <div className="card-surface p-6">
          <CheckCircle2 className="w-4 h-4 text-[#CCFF00] mb-3" />
          <p className="text-xs uppercase tracking-widest text-zinc-500">Published</p>
          <p className="font-display font-black text-3xl mt-1">{products.filter(p => p.is_published).length}</p>
        </div>
        <div className="card-surface p-6">
          <AlertCircle className="w-4 h-4 text-[#FFB800] mb-3" />
          <p className="text-xs uppercase tracking-widest text-zinc-500">Awaiting fee</p>
          <p className="font-display font-black text-3xl mt-1">{products.filter(p => !p.is_published).length}</p>
        </div>
        <div className="card-surface p-6">
          <DollarSign className="w-4 h-4 text-[#05D9E8] mb-3" />
          <p className="text-xs uppercase tracking-widest text-zinc-500">{subActive ? "Pro until" : "Plan"}</p>
          <p className="font-display font-bold text-xl mt-1">{subActive ? subUntil : "Pay-per-listing"}</p>
        </div>
      </div>

      {/* Products */}
      <h2 className="font-display font-bold text-2xl mb-6">Your products</h2>
      {products.length === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="seller-empty">
          <p className="text-zinc-400 mb-4">No listings yet — start glowing.</p>
          <Link to="/seller/new" className="btn-primary">Create first listing</Link>
        </div>
      ) : (
        <div className="overflow-x-auto border border-white/5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/5">
                <th className="p-4">Product</th>
                <th className="p-4">Category</th>
                <th className="p-4">Price</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} data-testid={`seller-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img src={img(p.images?.[0])} alt="" className="w-12 h-12 object-cover" />
                      <Link to={`/product/${p.id}`} className="font-bold hover:text-[#FF007F]">{p.title}</Link>
                    </div>
                  </td>
                  <td className="p-4 text-zinc-400">{p.category}</td>
                  <td className="p-4 font-display font-bold text-[#FF007F]">${Number(p.price).toFixed(2)}</td>
                  <td className="p-4">
                    {p.is_published ? (
                      isFeatured(p) ? (
                        <span data-testid={`status-featured-${p.id}`} className="text-[10px] uppercase tracking-widest text-[#CCFF00] border border-[#CCFF00]/40 px-2 py-1" style={{boxShadow: "0 0 10px rgba(204,255,0,0.35)"}}>★ Featured</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-[#CCFF00] border border-[#CCFF00]/30 px-2 py-1">Live</span>
                      )
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest text-[#FFB800] border border-[#FFB800]/30 px-2 py-1">Pay fee</span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex gap-2">
                      {!p.is_published && (
                        <button data-testid={`pay-fee-${p.id}`} onClick={() => payListing(p.id)} className="btn-primary !py-2 !px-3 !text-[10px]">Pay $0.50</button>
                      )}
                      {p.is_published && !isFeatured(p) && (
                        <button data-testid={`boost-${p.id}`} onClick={() => boostListing(p.id)} className="btn-secondary !py-2 !px-3 !text-[10px] inline-flex items-center gap-1 hover:!border-[#CCFF00] hover:!text-[#CCFF00]">
                          <Star className="w-3 h-3" /> Boost $2.99
                        </button>
                      )}
                      <button data-testid={`delete-${p.id}`} onClick={() => del(p.id)} className="p-2 text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
